import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setBottomTab, openModal } from '../../store/uiSlice';
import { removePosition, setBalance, removePendingOrder, updatePosition } from '../../store/tradeSlice';
import { showToast } from '../../store/uiSlice';
import { fmtP, fmtUSD } from '../../utils/formatters';
import { getLotSize, getInstrumentById } from '../../utils/constants';
import { calcForexPnl } from '../../utils/forexUtils';
import FullViewPanel from './FullViewPanel';
import api from '../../services/api';

const calcPnl = (isShort, currentPrice, avgPrice, qty, ls, instrumentId, cat) =>
  calcForexPnl(isShort, currentPrice, avgPrice, qty, instrumentId, cat, ls);

const fmtSL = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : '0.00');
const fmtTP = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : '0.00');

const fmtDT = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

const MIN_H = 36;
const DEF_H = 200;
const MAX_H = 520;

const getCategoryFromInstrument = (instrumentId) => {
  const instrument = getInstrumentById(instrumentId);
  return instrument?.cat || null;
};

/**
 * ✅ SINGLE SOURCE OF TRUTH for active-position P&L.
 * Row display, panel footer, and full-view footer all call this.
 */
const getPositionNetPnl = (pos, marketData) => {
  const rawQty       = Number(pos.qty) || 0;
  const qty          = Math.abs(rawQty);
  const avgPrice     = Number(pos.avgPrice) || 0;
  const cat          = pos.category || 'crypto';
  const isShort      = pos.direction === 'sell' || rawQty < 0;
  const currentPrice = Number(marketData[pos.instrumentId]?.price) || avgPrice;
  const ls           = getLotSize(cat, pos.instrumentId);
  const pnl          = calcPnl(isShort, currentPrice, avgPrice, qty, ls, pos.instrumentId, cat);
  const swap         = Number(pos.swap) || 0;
  const commission   = Number(pos.commission) || 0;
  return pnl - swap - commission;
};

/**
 * ✅ SINGLE SOURCE OF TRUTH for history-order P&L.
 * Backend's `order.pnl` already has commission deducted, so we only subtract swap here.
 */
const getHistoryNetPnl = (order) => {
  const pnl  = Number(order.pnl) || 0;
  const swap = Number(order.swap) || 0;
  return pnl - swap;
};

const Badge = ({ children, color, isLight }) => {
  const map = {
    long: isLight
      ? 'bg-[#059669] text-white border-[#059669]'
      : 'bg-[rgba(16,185,129,0.18)] text-[#34d399] border-[rgba(16,185,129,0.3)]',
    short: isLight
      ? 'bg-[#dc2626] text-white border-[#dc2626]'
      : 'bg-[rgba(239,68,68,0.18)] text-[#f87171] border-[rgba(239,68,68,0.3)]',
    orange: 'bg-[rgba(46,54,66,0.5)] text-[#94a3b8] border-[rgba(46,54,66,0.6)]',
    purple: 'bg-[rgba(139,92,246,0.18)] text-[#a78bfa] border-[rgba(139,92,246,0.3)]',
    teal: 'bg-[rgba(20,184,166,0.18)] text-[#2dd4bf] border-[rgba(20,184,166,0.3)]',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold font-mono border whitespace-nowrap w-fit ${map[color]}`}>
      {children}
    </span>
  );
};

const CloseBtn = ({ onClick, disabled, label = 'Close' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="px-2.5 py-1 rounded text-[10px] font-syne font-bold transition-all disabled:opacity-40 flex items-center gap-1"
    style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-redLight)' }}
    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.18)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-redBg)')}
  >
    <i className="fas fa-xmark text-[9px]" />{label}
  </button>
);

const getDirectionLabel = (trade) => {
  const isLong = trade.direction === 'buy';
  return { label: isLong ? 'LONG' : 'SHORT', color: isLong ? 'long' : 'short' };
};

const BottomPanel = () => {
  const dispatch = useDispatch();
  const { bottomTab } = useSelector((s) => s.ui);
  const { portfolio, pendingOrders, historyOrders, marketData, balance } = useSelector((s) => s.trade);
  const theme = useSelector((s) => s.ui.theme);
  const isLight = theme === 'light';

  const [isLoading,   setIsLoading]   = useState(false);
  const [panelHeight, setPanelHeight] = useState(DEF_H);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showFullView, setShowFullView] = useState(false);
  const isDragging = useRef(false);
  const startY     = useRef(0);
  const startH     = useRef(0);

  const [editingSlTp, setEditingSlTp] = useState(null);
  const [editSl, setEditSl] = useState('');
  const [editTp, setEditTp] = useState('');
  const [savingSlTp, setSavingSlTp] = useState(false);

  const [showPartialClose, setShowPartialClose] = useState(null);
  const [partialCloseQty, setPartialCloseQty] = useState('');
  const [partialClosing, setPartialClosing] = useState(false);

  const [totalCredit, setTotalCredit] = useState(0);
  const [bonusBalance, setBonusBalance] = useState(0);

  useEffect(() => {
    const fetchCreditAndBonus = async () => {
      try {
        const [financeRes, creditRes] = await Promise.all([
          api.get('/user/finance/summary'),
          api.get('/user/credit/requests'),
        ]);
        setBonusBalance(financeRes.data?.bonusBalance || 0);
        if (creditRes.data && Array.isArray(creditRes.data)) {
          const approved = creditRes.data
            .filter(r => r.status === 'approved')
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          setTotalCredit(approved);
        }
      } catch (e) {}
    };
    fetchCreditAndBonus();
  }, []);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    startY.current     = e.clientY;
    startH.current     = panelHeight;
    document.body.style.cursor     = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [panelHeight]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const delta = startY.current - e.clientY;
      const newH  = Math.min(MAX_H, Math.max(DEF_H, startH.current + delta));
      setPanelHeight(newH);
      if (isCollapsed) setIsCollapsed(false);
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current             = false;
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, [isCollapsed]);

  const toggleCollapse = () => setIsCollapsed(p => !p);

  // ✅ Active P&L now uses the SAME function as the rows: pnl - swap - commission
  const totalLivePnl = Object.values(portfolio).reduce(
    (acc, pos) => acc + getPositionNetPnl(pos, marketData),
    0
  );

  // ✅ History P&L uses the SAME function as history rows: pnl - swap
  const totalHistoryPnl = historyOrders.reduce(
    (acc, o) => acc + getHistoryNetPnl(o),
    0
  );

  const totalMarginUsed = Object.values(portfolio).reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const currentBalance  = Number(balance) || 0;
  const equity          = currentBalance + totalLivePnl + totalCredit + bonusBalance;
  const freeMargin      = equity - totalMarginUsed;
  const marginLevel     = totalMarginUsed > 0 ? (equity / totalMarginUsed) * 100 : 0;

  const handleClosePosition = async (positionId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const position     = portfolio[positionId];
      const currentPrice = marketData[position.instrumentId]?.price;
      if (!currentPrice) { alert('Price not available'); return; }
      const response = await api.post('/trade/close', {
        instrumentId: position.instrumentId,
        positionId: positionId,
        closePrice: currentPrice
      });
      if (response.data.success) {
        dispatch(setBalance(response.data.newBalance));
        dispatch(removePosition(positionId));
        dispatch(showToast({ message: 'Position closed', type: 'success' }));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to close position');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.delete(`/orders/${orderId}`);
      if (response.data.success) dispatch(removePendingOrder(orderId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel order');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditSlTp = (positionId, currentSl, currentTp) => {
    setEditingSlTp(positionId);
    setEditSl(Number(currentSl) > 0 ? String(Number(currentSl)) : '');
    setEditTp(Number(currentTp) > 0 ? String(Number(currentTp)) : '');
  };

  const cancelEditSlTp = () => {
    setEditingSlTp(null);
    setEditSl('');
    setEditTp('');
  };

  const saveSlTp = async (positionId) => {
    if (savingSlTp) return;
    setSavingSlTp(true);
    try {
      const sl = editSl ? Number(editSl) : 0;
      const tp = editTp ? Number(editTp) : 0;
      const response = await api.put(`/trade/update-sl-tp/${positionId}`, { sl, tp });
      if (response.data?.position) {
        dispatch(updatePosition({ instrumentId: response.data.position.instrumentId, position: response.data.position }));
        dispatch(showToast({ message: 'SL/TP updated', type: 'success' }));
      }
      cancelEditSlTp();
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || 'Failed to update SL/TP', type: 'error' }));
    } finally {
      setSavingSlTp(false);
    }
  };

  const openPartialClose = (positionId) => {
    setShowPartialClose(positionId);
    setPartialCloseQty('');
  };

  const closePartialCloseModal = () => {
    setShowPartialClose(null);
    setPartialCloseQty('');
  };

  const handlePartialClose = async () => {
    if (partialClosing || !showPartialClose) return;
    const qty = Number(partialCloseQty);
    const pos = portfolio[showPartialClose];
    const totalQty = Math.abs(Number(pos?.qty)) || 0;

    if (!qty || qty <= 0 || qty >= totalQty) {
      dispatch(showToast({ message: `Enter lots between 0.01 and ${(totalQty - 0.01).toFixed(2)}`, type: 'error' }));
      return;
    }

    setPartialClosing(true);
    try {
      const response = await api.post(`/trade/partial-close/${showPartialClose}`, { closeQuantity: qty });
      if (response.data.success) {
        dispatch(setBalance(response.data.newBalance));
        if (response.data.position) {
          dispatch(updatePosition({ instrumentId: response.data.instrumentId, position: response.data.position }));
        } else {
          dispatch(removePosition(showPartialClose));
        }
        dispatch(showToast({
          message: `Closed ${qty.toFixed(2)} lots · P&L: ${response.data.pnl >= 0 ? '+' : ''}$${response.data.pnl.toFixed(2)}`,
          type: 'success'
        }));
      }
      closePartialCloseModal();
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || 'Partial close failed', type: 'error' }));
    } finally {
      setPartialClosing(false);
    }
  };

  const ACTIVE_COLS = '60px 90px 68px 70px 110px 90px 130px 72px 72px 72px 110px 80px 1fr';
  const ACTIVE_HDRS = ['ID','INSTRUMENT','TYPE','AMOUNT','TRADE VOL','OPEN RATE','OPEN TIME','S/L','T/P','SWAP','TOTAL PROFIT','MARGIN','ACTION'];

  const renderActivePositions = () => {
    const positions = Object.entries(portfolio);
    if (positions.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 py-10">
          <i className="fas fa-inbox text-base" style={{ color: 'var(--color-textDim)' }} />
          <p className="font-mono text-xs" style={{ color: 'var(--color-textSecondary)' }}>No active positions · place a trade to get started</p>
        </div>
      );
    }

    return (
      <div style={{ minWidth: 1100 }}>
        <div className="grid gap-1 px-3 py-1.5 border-b sticky top-0 z-10"
          style={{ gridTemplateColumns: ACTIVE_COLS, background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
          {ACTIVE_HDRS.map(h => (
            <span key={h} className="text-[11px] font-mono font-extrabold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>{h}</span>
          ))}
        </div>

        {[...positions].sort(([, a], [, b]) => {
          const dateA = new Date(a.openTime || a.createdAt || 0);
          const dateB = new Date(b.openTime || b.createdAt || 0);
          return dateB - dateA;
        }).map(([id, pos]) => {
          const rawQty       = Number(pos.qty) || 0;
          const qty          = Math.abs(rawQty);
          const avgPrice     = Number(pos.avgPrice) || 0;
          const cat          = pos.category || 'crypto';
          const isShort      = pos.direction === 'sell' || rawQty < 0;
          const ls           = getLotSize(cat, pos.instrumentId);
          const tradeVolume  = (qty * ls).toLocaleString('en-US', { maximumFractionDigits: 2 });
          const swap         = Number(pos.swap) || 0;
          const margin       = Number(pos.cost) || 0;
          // ✅ Same function used by the footer total — guarantees row/footer match
          const totalProfit  = getPositionNetPnl(pos, marketData);
          const posId        = pos.id ? String(pos.id).slice(-6) : id.slice(0, 6);
          const isEditing    = editingSlTp === pos.id;

          return (
            <div
              key={id}
              className="grid gap-1 px-3 py-2 border-b transition-colors"
              style={{ gridTemplateColumns: ACTIVE_COLS, background: 'transparent', borderColor: 'var(--color-borderLight)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bgHover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{posId}</span>
              <span className="font-mono text-[12px] font-bold self-center flex items-center gap-1" style={{ color: 'var(--color-text)' }}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isShort ? 'bg-[#ef4444]' : 'bg-[#10b981]'}`} />
                {pos.instrumentId}
              </span>
              <Badge color={isShort ? 'short' : 'long'} isLight={isLight}>{isShort ? 'SHORT' : 'LONG'}</Badge>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{qty.toFixed(2)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{tradeVolume}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{fmtP(avgPrice, cat)}</span>
              <span className="font-mono text-[11px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{fmtDT(pos.openTime || pos.createdAt)}</span>

              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(pos.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>
                {isEditing ? (
                  <input type="number" value={editSl} onChange={e => setEditSl(e.target.value)} step="any"
                    className="w-16 rounded px-1.5 py-0.5 text-[11px] font-bold outline-none"
                    style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-redLight)' }} placeholder="SL" />
                ) : fmtSL(pos.sl)}
              </span>

              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(pos.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>
                {isEditing ? (
                  <input type="number" value={editTp} onChange={e => setEditTp(e.target.value)} step="any"
                    className="w-16 rounded px-1.5 py-0.5 text-[11px] font-bold outline-none"
                    style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-greenLight)' }} placeholder="TP" />
                ) : fmtTP(pos.tp)}
              </span>

              <span className="font-mono text-[12px] font-bold self-center" style={{ color: swap !== 0 ? 'var(--color-orangeLight)' : 'var(--color-textDim)' }}>
                {swap !== 0 ? (swap < 0 ? '-$' : '$') + Math.abs(swap).toFixed(2) : '$0.00'}
              </span>
              <div className="self-center">
                <div className="font-mono font-extrabold text-[13px]" style={{ color: totalProfit >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                  {totalProfit >= 0 ? '+$' : '-$'}{Math.abs(totalProfit).toFixed(2)}
                </div>
              </div>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>
                {margin > 0 ? '$' + margin.toFixed(2) : '—'}
              </span>
              <div className="self-center flex justify-end gap-1.5 flex-wrap">
                {isEditing ? (
                  <>
                    <button onClick={() => saveSlTp(pos.id)} disabled={savingSlTp}
                      className="px-2 py-1 rounded text-[9px] font-syne font-bold transition-all disabled:opacity-40"
                      style={{ background: 'var(--color-greenBg)', border: '1px solid var(--color-greenBorder)', color: 'var(--color-greenLight)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.22)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-greenBg)'}>
                      {savingSlTp ? '...' : '✓'}
                    </button>
                    <button onClick={cancelEditSlTp}
                      className="px-2 py-1 rounded text-[9px] font-syne font-bold transition-all"
                      style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-redLight)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.18)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-redBg)'}>✕</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEditSlTp(pos.id, pos.sl, pos.tp)}
                      className="px-2 py-1 rounded text-[9px] font-syne font-bold transition-all"
                      style={{ background: 'var(--color-purpleBg)', border: '1px solid var(--color-purpleBorder)', color: 'var(--color-purpleLight)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.18)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-purpleBg)'}>
                      <i className="fas fa-pen text-[8px] mr-0.5" />SL/TP
                    </button>
                    <button onClick={() => openPartialClose(id)}
                      className="px-2 py-1 rounded text-[9px] font-syne font-bold transition-all"
                      style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(46,54,66,0.55)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-orangeBg)'}>
                      <i className="fas fa-scissors text-[8px] mr-0.5" />Part
                    </button>
                    <CloseBtn onClick={() => handleClosePosition(id)} disabled={isLoading} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const PENDING_COLS = '60px 90px 110px 70px 100px 90px 90px 80px 72px 72px 1fr';
  const PENDING_HDRS = ['ID','SYMBOL','TYPE','LOTS','TRADE VOL','TARGET','CURRENT','DIST','S/L','T/P','ACTION'];

  const renderPendingOrders = () => {
    if (pendingOrders.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 py-10">
          <i className="fas fa-clock text-base" style={{ color: 'var(--color-textDim)' }} />
          <p className="font-mono text-xs" style={{ color: 'var(--color-textSecondary)' }}>No pending orders</p>
        </div>
      );
    }

    return (
      <div style={{ minWidth: 900 }}>
        <div className="grid gap-1 px-3 py-1.5 border-b sticky top-0 z-10"
          style={{ gridTemplateColumns: PENDING_COLS, background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
          {PENDING_HDRS.map(h => (
            <span key={h} className="text-[11px] font-mono font-extrabold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>{h}</span>
          ))}
        </div>

        {pendingOrders.filter(Boolean).map(order => {
          const qty          = Number(order.qty) || 0;
          const targetPrice  = Number(order.targetPrice) || 0;
          const currentPrice = Number(marketData[order.instId]?.price) || 0;
          const dist         = currentPrice ? ((targetPrice - currentPrice) / currentPrice * 100).toFixed(2) : '—';
          const isBuy        = order.direction === 'buy';
          const cat          = order.cat || getCategoryFromInstrument(order.instId) || 'forex';
          const ls           = getLotSize(cat, order.instId);
          const tradeVolume  = (qty * ls).toLocaleString();
          const ordId        = order.id ? String(order.id).slice(-6) : '—';

          return (
            <div
              key={order.id}
              className="grid gap-1 px-3 py-2 border-b transition-colors"
              style={{ gridTemplateColumns: PENDING_COLS, background: 'transparent', borderColor: 'var(--color-borderLight)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bgHover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{ordId}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{order.instId}</span>
              <Badge color={isBuy ? 'long' : 'short'} isLight={isLight}>{isBuy ? 'LONG' : 'SHORT'} {order.type?.toUpperCase()}</Badge>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{qty.toFixed(2)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{tradeVolume}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{fmtP(targetPrice, cat)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{fmtP(currentPrice, cat)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{dist}%</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(order.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>{fmtSL(order.sl)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(order.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>{fmtTP(order.tp)}</span>
              <div className="self-center flex justify-end">
                <CloseBtn onClick={() => handleCancelOrder(order.id)} disabled={isLoading} label="Cancel" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const HISTORY_COLS = '55px 80px 55px 60px 95px 80px 115px 80px 115px 65px 65px 70px 100px 65px 70px';
  const HISTORY_HDRS = ['ID','SYMBOL','TYPE','LOTS','TRADE VOL','ENTRY','OPEN TIME','EXIT','CLOSE TIME','S/L','T/P','SWAP','PROFIT','ROI','REASON'];

  const renderHistory = () => {
    if (historyOrders.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 py-10">
          <i className="fas fa-clock-rotate-left text-base" style={{ color: 'var(--color-textDim)' }} />
          <p className="font-mono text-xs" style={{ color: 'var(--color-textSecondary)' }}>No trade history yet</p>
        </div>
      );
    }

    return (
      <div style={{ minWidth: 1280 }}>
        <div className="grid gap-1 px-3 py-1.5 border-b sticky top-0 z-10"
          style={{ gridTemplateColumns: HISTORY_COLS, background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
          {HISTORY_HDRS.map(h => (
            <span key={h} className="text-[11px] font-mono font-extrabold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>{h}</span>
          ))}
        </div>

        {[...historyOrders].filter(Boolean).map((order, idx) => {
          const qty          = Number(order.qty)        || 0;
          const swap         = Number(order.swap)       || 0;
          const entryPrice   = Number(order.entryPrice) || 0;
          const exitPrice    = Number(order.exitPrice)  || 0;
          // ✅ Same function as footer totalHistoryPnl
          const netPnl       = getHistoryNetPnl(order);
          const instrumentId = order.instId || order.instrumentId;
          const cat          = order.cat || getCategoryFromInstrument(instrumentId) || 'forex';
          const ls           = getLotSize(cat, instrumentId);
          const tradeVolume  = (qty * ls).toLocaleString();
          const ordId        = order.id ? String(order.id).slice(-6) : String(idx + 1).padStart(6, '0');
          const closeReason  = order.closeReason || 'WebTrader';
          const dir          = getDirectionLabel(order);
          const notional     = entryPrice * qty * ls;
          const roi          = notional > 0 ? ((netPnl / notional) * 100) : 0;
          const reasonColor  = closeReason === 'TakeProfit' ? 'teal' : closeReason === 'StopLoss' ? 'short' : closeReason === 'PartialClose' ? 'orange' : 'purple';

          return (
            <div
              key={order.id ?? idx}
              className="grid gap-1 px-3 py-2 border-b transition-colors"
              style={{ gridTemplateColumns: HISTORY_COLS, background: 'transparent', borderColor: 'var(--color-borderLight)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bgHover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{ordId}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{instrumentId}</span>
              <Badge color={dir.color} isLight={isLight}>{dir.label}</Badge>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{qty.toFixed(2)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{tradeVolume}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{fmtP(entryPrice, cat)}</span>
              <span className="font-mono text-[11px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{fmtDT(order.openTime || order.time)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: 'var(--color-text)' }}>{fmtP(exitPrice, cat)}</span>
              <span className="font-mono text-[11px] font-bold self-center" style={{ color: 'var(--color-textSecondary)' }}>{fmtDT(order.closeTime)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(order.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>{fmtSL(order.sl)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: Number(order.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>{fmtTP(order.tp)}</span>
              <span className="font-mono text-[12px] font-bold self-center" style={{ color: swap !== 0 ? 'var(--color-orangeLight)' : 'var(--color-textDim)' }}>
                {swap !== 0 ? (swap < 0 ? '-$' : '$') + Math.abs(swap).toFixed(2) : '$0.00'}
              </span>
              <div className="self-center">
                <span className="font-mono font-extrabold text-[13px]" style={{ color: netPnl >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                  {netPnl >= 0 ? '+$' : '-$'}{Math.abs(netPnl).toFixed(2)}
                </span>
              </div>
              <div className="self-center">
                <span className="font-mono font-extrabold text-[12px]" style={{ color: roi >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                  {roi >= 0 ? '+' : ''}{roi.toFixed(2)}%
                </span>
              </div>
              <Badge color={reasonColor}>{closeReason}</Badge>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFooter = () => {
    if (isCollapsed) return null;

    const isHistoryTab = bottomTab === 'history';
    const isPendingTab = bottomTab === 'pending';
    const displayPnl   = isHistoryTab ? totalHistoryPnl : isPendingTab ? 0 : totalLivePnl;
    const label        = isHistoryTab ? 'Profit/Losses' : isPendingTab ? 'Pending Orders' : 'Unrealized P&L';
    const activeCount  = Object.keys(portfolio).length;

    const displayEquity      = isHistoryTab
      ? currentBalance + totalHistoryPnl + totalCredit + bonusBalance
      : isPendingTab
        ? currentBalance + totalCredit + bonusBalance
        : equity;
    const displayMarginUsed  = (isHistoryTab || isPendingTab) ? 0 : totalMarginUsed;
    const displayFreeMargin  = isHistoryTab
      ? currentBalance + totalHistoryPnl + totalCredit + bonusBalance
      : isPendingTab
        ? currentBalance + totalCredit + bonusBalance
        : freeMargin;
    const displayMarginLevel = (isHistoryTab || isPendingTab || totalMarginUsed === 0) ? null : marginLevel;

    return (
      <div
        className="flex items-center gap-3 px-4 py-2 border-t flex-shrink-0 flex-wrap"
        style={{ background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>
            {isHistoryTab ? 'Closed' : isPendingTab ? 'Pending' : 'Active'}
          </span>
          <span className="font-mono text-[12px] font-extrabold" style={{ color: 'var(--color-text)' }}>
            {isHistoryTab ? historyOrders.length : isPendingTab ? pendingOrders.length : activeCount}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>Equity</span>
          <span className="font-mono text-[12px] font-extrabold" style={{ color: 'var(--color-text)' }}>{fmtUSD(displayEquity)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>Margin Used</span>
          <span className="font-mono text-[12px] font-extrabold" style={{ color: 'var(--color-yellow)' }}>{fmtUSD(displayMarginUsed)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>Free Margin</span>
          <span className="font-mono text-[12px] font-extrabold" style={{ color: displayFreeMargin >= 0 ? 'var(--color-greenLight)' : 'var(--color-red)' }}>
            {fmtUSD(displayFreeMargin)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>Margin Level</span>
          <span className="font-mono text-[12px] font-extrabold" style={{ color: displayMarginLevel === null ? 'var(--color-textSecondary)' : displayMarginLevel > 200 ? 'var(--color-greenLight)' : displayMarginLevel > 100 ? 'var(--color-yellow)' : 'var(--color-red)' }}>
            {displayMarginLevel !== null ? displayMarginLevel.toFixed(1) + '%' : '—'}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wide font-bold" style={{ color: 'var(--color-textSecondary)' }}>{label}:</span>
          <span className="font-mono font-extrabold text-[14px]" style={{ color: displayPnl >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
            {displayPnl !== 0 ? (displayPnl >= 0 ? '+' : '') + fmtUSD(Math.abs(displayPnl)) : fmtUSD(0)}
          </span>
        </div>
      </div>
    );
  };

  const contentH = isCollapsed ? 0 : panelHeight - MIN_H - 26;

  const partialClosePos = showPartialClose ? portfolio[showPartialClose] : null;
  const partialCloseTotalQty = partialClosePos ? Math.abs(Number(partialClosePos.qty)) : 0;
  const partialCloseQtyNum = Number(partialCloseQty) || 0;
  const partialCloseRemaining = partialCloseTotalQty - partialCloseQtyNum;

  return (
    <div
      className="flex-shrink-0 border-t flex flex-col"
      style={{
        height:     isCollapsed ? MIN_H : panelHeight,
        background: 'var(--color-bg)',
        borderColor: 'var(--color-borderStrong)',
        transition: isDragging.current ? 'none' : 'height 0.18s ease',
      }}
    >
      <div
        onMouseDown={onMouseDown}
        className="w-full flex-shrink-0 flex items-center justify-center cursor-ns-resize group"
        style={{ height: 8, background: 'transparent' }}
        title="Drag to resize"
      >
        <div
          className="rounded-full transition-all group-hover:opacity-100"
          style={{ width: 36, height: 3, background: 'var(--color-borderStrong)', opacity: 0.4 }}
        />
      </div>

      <div
        className="flex items-center border-b px-3 flex-shrink-0"
        style={{ height: MIN_H - 8, borderColor: 'var(--color-borderMedium)' }}
      >
        {[
          { key: 'active',  label: 'Active',  icon: 'fa-circle-check' },
          { key: 'pending', label: 'Pending', icon: 'fa-clock' },
          { key: 'history', label: 'History', icon: 'fa-clock-rotate-left' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => dispatch(setBottomTab(tab.key))}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-syne font-bold cursor-pointer border-b-2 transition-all"
            style={{
              color: bottomTab === tab.key ? 'var(--color-text)' : 'var(--color-textSecondary)',
              borderColor: bottomTab === tab.key ? 'var(--color-orange)' : 'transparent',
            }}
          >
            <i className={`fas ${tab.icon} text-[10px]`} />
            {tab.label}
            <span className="ml-0.5 text-[9px] px-1.5 py-0.5 rounded-full"
              style={{
                background: bottomTab === tab.key ? 'var(--color-orangeBg)' : 'var(--color-bgCard)',
                color: bottomTab === tab.key ? 'var(--color-text)' : 'var(--color-textSecondary)',
              }}>
              {tab.key === 'active'
                ? Object.keys(portfolio).length
                : tab.key === 'pending'
                  ? pendingOrders.length
                  : historyOrders.length}
            </span>
          </button>
        ))}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowFullView(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded font-syne text-[11px] font-bold transition-all"
            style={{ background: 'var(--color-purpleBg)', border: '1px solid var(--color-purpleBorder)', color: 'var(--color-purpleLight)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(139,92,246,0.18)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-purpleBg)')}
          >
            <i className="fas fa-up-right-and-down-left-from-center text-[9px]" /> Full View
          </button>
          <button
            onClick={() => dispatch(openModal('orderModal'))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded font-syne text-[11px] font-bold transition-all"
            style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(46,54,66,0.55)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-orangeBg)')}
          >
            <i className="fas fa-plus text-[9px]" /> New Order
          </button>
          <button
            onClick={toggleCollapse}
            className="flex items-center justify-center w-6 h-6 rounded transition-all"
            style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderStrong)', color: 'var(--color-textSecondary)' }}
            title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-text)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-textSecondary)')}
          >
            <i className={`fas ${isCollapsed ? 'fa-chevron-up' : 'fa-chevron-down'} text-[9px]`} />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="overflow-auto flex-1 bottom-panel-scroll" style={{ maxHeight: contentH }}>
          {bottomTab === 'active'  && renderActivePositions()}
          {bottomTab === 'pending' && renderPendingOrders()}
          {bottomTab === 'history' && renderHistory()}
        </div>
      )}

      {renderFooter()}

      {/* Partial Close Modal */}
      {showPartialClose && partialClosePos && (
        <div className="fixed inset-0 z-[900] flex items-center justify-center">
          <div className="absolute inset-0" style={{ background: 'var(--color-bgOverlay)' }} onClick={closePartialCloseModal} />
          <div className="relative rounded-2xl w-full max-w-md mx-4 shadow-2xl" style={{ background: 'var(--color-bgSurface)', border: '2px solid var(--color-orangeBorder)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-borderStrong)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
                  <i className="fas fa-scissors" style={{ color: 'var(--color-text)' }} />
                </div>
                <div>
                  <h3 className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>PARTIAL CLOSE</h3>
                  <p className="text-[10px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>{partialClosePos.instrumentId} · {partialCloseTotalQty.toFixed(2)} lots total</p>
                </div>
              </div>
              <button onClick={closePartialCloseModal} className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-red)'; e.currentTarget.style.color = 'var(--color-red)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderHover)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-mono font-bold uppercase tracking-wider block mb-2" style={{ color: 'var(--color-textSecondary)' }}>
                  Lots to Close (1 - {(partialCloseTotalQty - 0.01).toFixed(2)})
                </label>
                <input
                  type="number"
                  value={partialCloseQty}
                  onChange={e => setPartialCloseQty(e.target.value)}
                  step="0.01" min="0.01" max={(partialCloseTotalQty - 0.01).toFixed(2)}
                  placeholder="Enter lot size"
                  className="w-full rounded-lg px-4 py-3 font-mono text-sm font-bold outline-none transition-all"
                  style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-text)' }}
                  onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'}
                  autoFocus
                />
              </div>
              {partialCloseQtyNum > 0 && partialCloseQtyNum < partialCloseTotalQty && (
                <div className="p-3 rounded-lg space-y-2" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-mono" style={{ color: 'var(--color-textSecondary)' }}>Closing</span>
                    <span className="font-mono font-bold" style={{ color: 'var(--color-redLight)' }}>{partialCloseQtyNum.toFixed(2)} lots</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-mono" style={{ color: 'var(--color-textSecondary)' }}>Remaining</span>
                    <span className="font-mono font-bold" style={{ color: 'var(--color-greenLight)' }}>{partialCloseRemaining.toFixed(2)} lots</span>
                  </div>
                  <div className="border-t pt-2 text-[9px] font-mono" style={{ borderColor: 'var(--color-borderMedium)', color: 'var(--color-textSecondary)' }}>
                    Margin will be recalculated for the remaining position. P&L for closed lots will be added to balance.
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={closePartialCloseModal}
                  className="flex-1 py-2.5 rounded-lg font-syne font-bold text-xs transition-all"
                  style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}>CANCEL</button>
                <button onClick={handlePartialClose}
                  disabled={partialClosing || !partialCloseQtyNum || partialCloseQtyNum <= 0 || partialCloseQtyNum >= partialCloseTotalQty}
                  className="flex-1 py-2.5 rounded-lg font-syne font-bold text-xs transition-all disabled:opacity-40"
                  style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}
                  onMouseEnter={e => { if (!partialClosing) e.currentTarget.style.background = 'rgba(46,54,66,0.55)'; }}
                  onMouseLeave={e => { if (!partialClosing) e.currentTarget.style.background = 'var(--color-orangeBg)'; }}>
                  {partialClosing ? 'CLOSING...' : 'CONFIRM PARTIAL CLOSE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FullViewPanel isOpen={showFullView} onClose={() => setShowFullView(false)} />

      <style>{`
        .bottom-panel-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
        .bottom-panel-scroll::-webkit-scrollbar-track { background: transparent; }
        .bottom-panel-scroll::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
        .bottom-panel-scroll::-webkit-scrollbar-thumb:hover { background: var(--color-scrollbarThumbHover); }
        .bottom-panel-scroll::-webkit-scrollbar-corner { background: transparent; }
        .bottom-panel-scroll { scrollbar-width: auto; scrollbar-color: var(--color-scrollbarThumb) transparent; }
      `}</style>
    </div>
  );
};

export default BottomPanel;